import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Item } from '@/lib/schemas';

interface UseItemsOptions {
  searchQuery?: string;
  page?: number;
  limit?: number;
}

export function useItems(options: UseItemsOptions = {}) {
  const { searchQuery, page = 1, limit = 10 } = options;

  return {
    getItems: useQuery<{data: Item[], totalCount: number}, Error>({
      queryKey: ['items', searchQuery, page, limit],
      queryFn: () => api.getItems({ search: searchQuery, page, limit }),
      staleTime: 5 * 60 * 1000, // 5 minutes
    }),
    getItemPriceComparison: (itemId: string) =>
      useQuery({
        queryKey: ['item-price-comparison', itemId],
        queryFn: () => api.getItemPriceComparison(itemId),
        enabled: !!itemId,
      }),
  };
}

export function useItem(id: string) {
  return useQuery<Item | null, Error>({
    queryKey: ['items', id],
    queryFn: () => api.getItem(id),
    enabled: !!id,
    staleTime: 0,
    gcTime: 0,
  });
}

export function useCreateItem() {
  const queryClient = useQueryClient();
  return useMutation<Item, Error, Omit<Item, 'item_id' | 'created_at' | 'updated_at'>>({
    mutationFn: (data) => api.createItem(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
    },
  });
}

export function useUpdateItem() {
  const queryClient = useQueryClient();
  return useMutation<Item | null, Error, { id: string; data: Partial<Omit<Item, 'item_id' | 'created_at'>>}>({
    mutationFn: ({ id, data }) => api.updateItem(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
    },
  });
}

export function useDeleteItem() {
  const queryClient = useQueryClient();
  return useMutation<boolean, Error, string>({
    mutationFn: (id) => api.deleteItem(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
    },
  });
}
